import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Locale = "zh-CN" | "en";

const messages = {
  "zh-CN": {
    appName: "合作伙伴门户",
    appTagline: "Bridge AI Gaming",
    signIn: "登录",
    signOut: "退出登录",
    username: "用户名",
    password: "密码",
    welcome: "欢迎回来",
    loginHelp: "仅限受邀合作伙伴和 BAI 管理员。请使用已分配的账号登录。",
    signInFailed: "无法登录。请检查用户名和密码。",
    setPassword: "设置密码",
    setPasswordHelp:
      "请输入一个至少 12 个字符的新密码，其中包含大小写字母、数字和符号。",
    newPassword: "新密码",
    confirmPassword: "确认新密码",
    savePassword: "保存密码",
    passwordMismatch: "两次输入的密码不一致。",
    passwordSetupFailed: "无法设置密码。请检查密码要求后重试。",
    activateAccount: "激活合作伙伴账号",
    invitationHelp:
      "请输入您的姓名并设置密码。此邀请链接仅可使用一次，并在 72 小时后失效。",
    displayName: "姓名",
    accountActivated: "账号已激活。请使用以下邮箱和您刚设置的密码登录。",
    goToSignIn: "前往登录",
    invitationInvalid: "邀请链接无效。请向 BAI 管理员索取新的邀请。",
    invitationFailed:
      "无法激活账号。邀请可能已过期或已被使用，请联系 BAI 管理员。",
    invitationCopied: "邀请已创建，72 小时注册链接已复制。",
    demoMode: "演示模式",
    demoModeHint: "示例数据仅供 ESAC 演示使用，未连接到生产系统或 WhatsApp。",
    demoWhatsAppTitle: "WhatsApp 群提醒演示",
    demoWhatsAppGroup: "ESAC - Bridge Partner Portal",
    demoWhatsAppMessage:
      "您好，本周有 8 个项目需要更新。请登录门户完成状态和下次跟进日期的更新。",
    demoWhatsAppStatus: "模拟状态：已发送至群组 · 周一 10:00",
    opportunities: "项目机会",
    overview: "概览",
    admin: "管理中心",
    audit: "审计记录",
    active: "进行中",
    overdue: "逾期",
    dueThisWeek: "本周到期",
    confirmed: "已确认 / 已付款",
    nextCheckIn: "下次跟进",
    update: "快速更新",
    status: "状态",
    note: "备注（可选）",
    reason: "原因",
    reviewDate: "复核日期",
    cancel: "取消",
    save: "保存更新",
    saved: "更新已保存",
    all: "全部",
    filter: "筛选项目",
    opportunityTools: "项目搜索和筛选",
    search: "搜索项目",
    searchPlaceholder: "搜索客户、市场、项目名称或概要",
    clearSearch: "清除搜索",
    showingResults: "显示 {visible} / {total} 个项目",
    projectSummary: "项目概要",
    client: "客户",
    market: "市场",
    lastUpdated: "最后更新",
    noResults: "没有符合条件的项目",
    adminIntro: "维护项目分配、邀请和每周跟进。",
    importCsv: "导入 CSV",
    addOpportunity: "新增项目",
    projectName: "项目名称",
    email: "邮箱",
    createInvite: "创建邀请",
    invitations: "邀请管理",
    reminderCopy: "WhatsApp 和邮件提醒文案",
    reminderSchedule: "每周提醒时间",
    weekly: "每周",
    scheduleIntro: "选择系统向 ESAC WhatsApp 群和邮箱发送合并提醒的时间。",
    reminderDay: "星期",
    reminderHour: "时间",
    timezone: "时区",
    saveSchedule: "保存提醒时间",
    scheduleSaved: "提醒时间已保存",
    monday: "星期一",
    tuesday: "星期二",
    wednesday: "星期三",
    thursday: "星期四",
    friday: "星期五",
    saturday: "星期六",
    sunday: "星期日",
    whatsApp: "WhatsApp",
    emailReminder: "邮件",
    reminderWhatsappText:
      "您好，本周有 {count} 个项目需要更新。请登录 Bridge 合作伙伴门户完成状态和下次跟进日期的更新。谢谢！",
    reminderEmailSubject: "Bridge 合作伙伴门户：本周有 {count} 个项目需要更新",
    reminderEmailText:
      "您好，\n\n本周有 {count} 个项目需要更新。请登录 Bridge 合作伙伴门户完成状态和下次跟进日期的更新。\n\n谢谢！",
    copyWhatsApp: "复制 WhatsApp 文案",
    copyEmail: "复制邮件文案",
    pending: "待接受",
    accepted: "已接受",
    revoked: "已撤销",
    expires: "到期",
    roleAdmin: "BAI 管理员",
    roleSupplier: "供应商更新员",
    english: "English",
    chinese: "简体中文",
    requiredReason: "暂停或关闭不再推进状态必须填写原因。",
    requiredReview: "暂停状态必须填写复核日期。",
    requiredCheckIn: "请选择下次跟进日期。",
    terminalHint: "只有已完成或关闭不再推进后才会停止提醒。",
    history: "更新历史",
    projectHistory: "客户 / 项目更新历史",
    noHistory: "暂无更新记录",
    attachments: "附件",
    addDocuments: "添加文件（可选）",
    documentHint:
      "最多 5 个文件，每个不超过 10 MB。支持 PDF、Office、文本和图片。",
    removeFile: "移除",
    tooManyFiles: "每次更新最多可添加 5 个文件。",
    fileTooLarge: "每个文件不得超过 10 MB。",
    statusChanged: "状态变更",
    previousStatus: "原状态",
    downloadFile: "打开附件",
    passwordReset: "管理员可在管理中心发起密码重置。",
  },
  en: {
    appName: "Partner Portal",
    appTagline: "Bridge AI Gaming",
    signIn: "Sign in",
    signOut: "Sign out",
    username: "Username",
    password: "Password",
    welcome: "Welcome back",
    loginHelp:
      "Invited partners and BAI administrators only. Sign in with your assigned account.",
    signInFailed: "Unable to sign in. Check your username and password.",
    setPassword: "Set your password",
    setPasswordHelp:
      "Choose a new password with at least 12 characters, including upper and lower case letters, a number, and a symbol.",
    newPassword: "New password",
    confirmPassword: "Confirm new password",
    savePassword: "Save password",
    passwordMismatch: "The passwords do not match.",
    passwordSetupFailed:
      "Unable to set your password. Check the requirements and try again.",
    activateAccount: "Activate partner account",
    invitationHelp:
      "Enter your name and choose a password. This invitation link works once and expires after 72 hours.",
    displayName: "Your name",
    accountActivated:
      "Your account is active. Sign in with the email below and the password you just created.",
    goToSignIn: "Continue to sign in",
    invitationInvalid:
      "This invitation link is invalid. Ask a BAI administrator for a new invitation.",
    invitationFailed:
      "The account could not be activated. The invitation may be expired or already used. Contact a BAI administrator.",
    invitationCopied: "Invitation created. The 72-hour signup link was copied.",
    demoMode: "Demo mode",
    demoModeHint:
      "Sample data for the ESAC demonstration only. It is not connected to production or WhatsApp.",
    demoWhatsAppTitle: "WhatsApp group reminder preview",
    demoWhatsAppGroup: "ESAC - Bridge Partner Portal",
    demoWhatsAppMessage:
      "Hello, 8 opportunities need an update this week. Please sign in and update the status and next check-in date.",
    demoWhatsAppStatus: "Simulated: delivered to group · Monday 10:00",
    opportunities: "Opportunities",
    overview: "Overview",
    admin: "Admin center",
    audit: "Audit history",
    active: "Active",
    overdue: "Overdue",
    dueThisWeek: "Due this week",
    confirmed: "Confirmed / paid",
    nextCheckIn: "Next check-in",
    update: "Quick update",
    status: "Status",
    note: "Note (optional)",
    reason: "Reason",
    reviewDate: "Review date",
    cancel: "Cancel",
    save: "Save update",
    saved: "Update saved",
    all: "All",
    filter: "Filter opportunities",
    opportunityTools: "Opportunity search and filters",
    search: "Search opportunities",
    searchPlaceholder: "Search client, market, project name, or summary",
    clearSearch: "Clear search",
    showingResults: "Showing {visible} of {total} opportunities",
    projectSummary: "Project summary",
    client: "Client",
    market: "Market",
    lastUpdated: "Last updated",
    noResults: "No opportunities match this filter",
    adminIntro: "Manage assignments, invitations, and weekly follow-up.",
    importCsv: "Import CSV",
    addOpportunity: "Add opportunity",
    projectName: "Project name",
    email: "Email",
    createInvite: "Create invitation",
    invitations: "Invitations",
    reminderCopy: "WhatsApp and email reminder copy",
    reminderSchedule: "Weekly reminder schedule",
    weekly: "Weekly",
    scheduleIntro:
      "Choose when the consolidated reminder is sent to the ESAC WhatsApp group and email recipients.",
    reminderDay: "Day",
    reminderHour: "Time",
    timezone: "Time zone",
    saveSchedule: "Save reminder schedule",
    scheduleSaved: "Reminder schedule saved",
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
    whatsApp: "WhatsApp",
    emailReminder: "Email",
    reminderWhatsappText:
      "Hello, {count} opportunities need an update this week. Please sign in to the Bridge Partner Portal and update the status and next check-in date. Thank you!",
    reminderEmailSubject:
      "Bridge Partner Portal: {count} opportunities need an update this week",
    reminderEmailText:
      "Hello,\n\n{count} opportunities need an update this week. Please sign in to the Bridge Partner Portal and update the status and next check-in date.\n\nThank you!",
    copyWhatsApp: "Copy WhatsApp copy",
    copyEmail: "Copy email copy",
    pending: "Pending",
    accepted: "Accepted",
    revoked: "Revoked",
    expires: "Expires",
    roleAdmin: "BAI administrator",
    roleSupplier: "Supplier updater",
    english: "English",
    chinese: "简体中文",
    requiredReason:
      "A reason is required for On hold or Closed - not proceeding.",
    requiredReview: "A review date is required for On hold.",
    requiredCheckIn: "Select the next check-in date.",
    terminalHint:
      "Reminders stop only after Completed or Closed - not proceeding.",
    history: "Update history",
    projectHistory: "Client / project update history",
    noHistory: "No updates have been recorded yet",
    attachments: "Attachments",
    addDocuments: "Add documents (optional)",
    documentHint:
      "Up to 5 files, maximum 10 MB each. PDF, Office, text, and images are supported.",
    removeFile: "Remove",
    tooManyFiles: "You can attach up to 5 files per update.",
    fileTooLarge: "Each file must be 10 MB or smaller.",
    statusChanged: "Status changed",
    previousStatus: "Previous status",
    downloadFile: "Open attachment",
    passwordReset:
      "Administrators can start password resets in the admin center.",
  },
} as const;

type MessageKey = keyof (typeof messages)["en"];

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (
    key: MessageKey,
    replacements?: Record<string, string | number>,
  ) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = localStorage.getItem("bridge-locale");
    return stored === "en" ? "en" : "zh-CN";
  });

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale(next) {
        localStorage.setItem("bridge-locale", next);
        document.documentElement.lang = next;
        setLocaleState(next);
      },
      t(key, replacements = {}) {
        let message: string = messages[locale][key];
        for (const [name, replacement] of Object.entries(replacements)) {
          message = message.replace(`{${name}}`, String(replacement));
        }
        return message;
      },
    }),
    [locale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}
